import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { ModalDirective } from 'ng-uikit-pro-standard';
import { BundleBuilderStore } from './bundle-builder.store';
import { Bundle, BundleItem, BundleLine, BundleLineDto, Category } from './bundle-builder.model';
import { tap } from 'rxjs/operators';
import { BundleBuilderApiService } from './bundle-builder-api.service';
import { BehaviorSubject, Subscription } from 'rxjs';
import { MessageTopicsEnum } from '../../shared-core/models/component-message.model';
import { ComponentMessagingService } from '../../shared-core/services/component-messaging.service';
import { GtagService } from '../../shared-core/services/gtag.service';
import { Item } from '../../shared-core/models/cart-data.model';

@Component({
  selector: 'app-bundle-builder',
  templateUrl: './bundle-builder.component.html',
  styleUrls: ['./bundle-builder.component.scss']
})
export class BundleBuilderComponent implements AfterViewInit, OnDestroy {
  @ViewChild('builderModal') builderModal: ModalDirective;
  private bodyElement: HTMLElement;
  IsWaiting: boolean;
  private _show: boolean;
  private _anchorWebCode: string;
  gtagClass: string;
  anchor$ = this.store.anchor$;
  modified$ = this.store.modified$;
  bundle$ = this.store.bundle$.pipe(
    tap((b) => {
      this.bundle = b;
      this.bundlePrice = b.price;
      const linesTotal = b.lines.reduce((acc, curr) => acc + curr.item.SellingPrice * curr.quantity, 0);
      this.bundleSavings = linesTotal - this.bundlePrice;
    })
  );
  filteredBundleItems$ = this.store.filteredBundleItems$;
  categories$ = this.store.categories$;
  currentCategory$ = this.store.currentCategory$;
  bundlePrice: number;
  bundleSavings: number;
  bundle: Bundle;
  private _bundleId: number;
  private ngAfterViewInitSubject$ = new BehaviorSubject<boolean>(false);

  constructor(
    private store: BundleBuilderStore,
    private api: BundleBuilderApiService,
    private messaging: ComponentMessagingService,
    private gtagService: GtagService
  ) { }

  ngOnDestroy(): void {
    this.ngAfterViewInitSubject$.unsubscribe();
    this.store.storeWaitingSubject.unsubscribe();
  }

  ngAfterViewInit() {
    this.bodyElement = document.querySelector('body');
    this.ngAfterViewInitSubject$.next(true);
  }

  @Input('show') set show(val: boolean) {
    this._show = val;
    this.executeShow().finally(() => {
      this.showChange.emit(val);
    });
  }

  get show(): boolean {
    return this._show;
  }
  @Output() showChange = new EventEmitter<boolean>();

  @Output() bundleUpdated = new EventEmitter();

  @Input('anchor') set anchorWebcode(val: string) {
    this._anchorWebCode = val;
    this.executeShow();
  }

  @Input('gtag-id') set gtagId(val: string) {
    this.gtagClass = val;
  }

  @Input('bundle-id') set bundleId(val: number) {
    this._bundleId = val;
    this.executeShow();
  }

  get anchorWebcode(): string {
    return this._anchorWebCode;
  }

  @Input('data-bundle-lines') set bundleLines(val: BundleLineDto[]) {
    this.store.cartBundle = val;
  }

  async executeShow() {
    if (!this._show) {
      if (this.builderModal?.isShown) {
        this.builderModal.hide();
        this.bodyElement.style.overflow = 'inherit';
      }
      return;
    }
    if (!this._anchorWebCode || !this._bundleId) {
      return;
    }
    try {
      this.store.storeWaitingSubject.subscribe((waiting) => {
        this.IsWaiting = waiting;
      });
      await this.store.init(this._bundleId);
      if (this.store.cartBundle?.length > 0) {
        const cartBundle = this.store.cartBundle
          .map((cbd) => {
            return {
              quantity: cbd.Quantity,
              item: this.store.bundleDefinition.find((bd) => bd.Webcode == cbd.Webcode)
            } as BundleLine;
          })
          .filter((i) => i.item);
        cartBundle.forEach((bl) => {
          const item = this.store.bundleDefinition.find((d) => d.Webcode == bl.item.Webcode);
          item.RequestedQuantity = bl.quantity;
          item.IsInCart = true;
        });
        await this.store.setCartBundle(cartBundle);
      }
      const anchor = this.store.bundleDefinition.find((i) => i.Webcode == this._anchorWebCode);
      await this.store.setAnchor(anchor);
      this.store.setCategory();
      this.ngAfterViewInitSubject$.subscribe((ready) => {
        if (!ready) {
          return;
        }
        this.builderModal.show();
        this.bodyElement.style.overflow = 'hidden';
      });
    } catch (error) {
      this._show = false;
      this.bodyElement.style.overflow = 'inherit';
      throw error;
    }
  }

  onClosed(event: Event) {
    this._show = false;
    this.showChange.emit(this._show);
    this.bodyElement.style.overflow = 'inherit';
    this.messaging.sendMessage(MessageTopicsEnum.CloseBundleBuilderRequest, false);
  }

  addToBundle(event: Event, item: BundleItem) {
    event.preventDefault();
    this.store.addItem(item);
  }

  removeLine(line: BundleLine) {
    line.item.IsInCart = false;
    this.store.remove(line);
  }

  async addToCart(event: Event) {
    this.store.storeWaitingSubject.next(true);
    event.preventDefault();
    this.sendBundleUpdateEvent();
    await this.api.addToCart(this.bundle.lines);
    this.bundleUpdated.emit();
    this.builderModal.hide();
    this.bodyElement.style.overflow = 'inherit';
    this.store.storeWaitingSubject.next(false);
  }

  categoryClick(event: Event, category: Category) {
    event.preventDefault();
    this.store.setCategory(category);
  }

  sendBundleUpdateEvent() {
    //find first all removed items from cart to update gtm
    const removedItems = this.store.cartBundle
      .map((x) => {
        const bundleItem = this.bundle.lines.find((b) => b.item.Webcode === x.Webcode);
        //exclude free items as they can not change in ui
        const itemDefinition = this.store.bundleDefinition.find(
          (i) => x.Webcode === i.Webcode && i.BundleId == this._bundleId // string == number
        );
        if (!bundleItem && itemDefinition) {
          return this.convertBundleItemToItem(
            {
              item: {
                Webcode: x.Webcode
              },
              quantity: x.Quantity
            } as BundleLine,
            itemDefinition
          );
        }
      })
      .filter((x) => x);

    this.gtagService.removeItemsFromCart(removedItems);
    // find new added items or qty changed
    const addedItems = this.bundle.lines
      .map((x) => {
        const cartItem = this.store.cartBundle.find((b) => b.Webcode === x.item.Webcode);
        if (!cartItem || cartItem.Quantity !== x.quantity) {
          const itemDefinition = this.store.bundleDefinition.find(
            (i) => x.item.Webcode === i.Webcode && i.BundleId == this._bundleId
          );
          return this.convertBundleItemToItem(x, itemDefinition);
        }
      })
      .filter((x) => x);

    this.gtagService.addItemsToCart(addedItems);
  }

  private convertBundleItemToItem(x: BundleLine, itemDefinition: BundleItem): Item {
    return {
      Webcode: x.item.Webcode,
      Description: itemDefinition.Description,
      Manufacturer: itemDefinition.Manufacturer,
      CategoryLevel1: itemDefinition.Category,
      UnitPrice: itemDefinition.SellingPrice,
      Quantity: x.quantity
    } as Item;
  }
}
